import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { UserEditModal } from './UserEditModal';
import { useAuth } from '../../hooks/useAuth';
import { getRoleDisplayName } from '../../utils/roleTranslations';
import './UserManagement.css';
import { User, Edit, Repeat, Trash, Laptop,
  Trash2, Users, ShieldMinus, ShieldCheck,
  SmartphoneNfc, Lock, Unlock, OctagonAlert,
 } from 'lucide-react';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  twoFactorEnabled: boolean;
  vacationDays: number;
  weeklyHours: number;
  workDays: string;
  maxWorkHours: number;
  startDate: string;
  nfcId: string | null;
  webTimeTrackingEnabled: boolean;
  initialOvertimeMinutes: number;
  lastLogin: string | null;
  createdAt: string;
  _count: {
    workLogs: number;
    timeOffs: number;
  };
}

interface UserManagementProps {
  onStatsUpdate: () => void;
}

export const UserManagement: React.FC<UserManagementProps> = ({ onStatsUpdate }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [, setMessage] = useState({ type: '', text: '' });
  //const [message, setMessage] = useState({ type: '', text: '' });
  const [sortConfig, setSortConfig] = useState<{
    key: keyof User | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });

  useEffect(() => {
    loadUsers();
  }, [filters, pagination.page]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;

      const response = await adminAPI.getAllUsers(params);
      
      let filteredUsers = response.data.users;
      
      if (filters.status === 'active') {
        filteredUsers = filteredUsers.filter((user: User) => user.isActive);
      } else if (filters.status === 'inactive') {
        filteredUsers = filteredUsers.filter((user: User) => !user.isActive);
      }

      setUsers(filteredUsers);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Error loading users:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Benutzer' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (user: User) => {
    const action = user.isActive ? 'deaktivieren' : 'aktivieren';
    if (!confirm(`Möchten Sie ${user.firstName} ${user.lastName} wirklich ${action}?`)) {
      return;
    }

    try {
      await adminAPI.toggleUserStatus(user.id);
      setMessage({ 
        type: 'success', 
        text: `Benutzer erfolgreich ${user.isActive ? 'deaktiviert' : 'aktiviert'}` 
      });
      loadUsers();
      onStatsUpdate();
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || `Fehler beim ${action} des Benutzers` 
      });
    }
  };

  const handleReset2FA = async (user: User) => {
    if (!confirm(`2FA für ${user.firstName} ${user.lastName} zurücksetzen?\n\nDies deaktiviert 2FA komplett und löscht alle Backup-Codes!`)) {
      return;
    }

    try {
      await adminAPI.reset2FA(user.id);
      setMessage({ type: 'success', text: '2FA erfolgreich zurückgesetzt' });
      loadUsers();
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Fehler beim Zurücksetzen von 2FA' 
      });
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (user._count.workLogs > 0 || user._count.timeOffs > 0) {
      if (!confirm(`WARNUNG: ${user.firstName} ${user.lastName} hat ${user._count.workLogs} Arbeitszeiten und ${user._count.timeOffs} Abwesenheiten!\n\nAlle Daten werden PERMANENT gelöscht. Möchten Sie trotzdem fortfahren?`)) {
        return;
      }
    }

    if (!confirm(`Möchten Sie ${user.firstName} ${user.lastName} wirklich PERMANENT löschen? Diese Aktion kann nicht rückgängig gemacht werden!`)) {
      return;
    }

    const confirmText = `${user.firstName} ${user.lastName}`;
    const userInput = prompt(`Zur Bestätigung geben Sie "${confirmText}" ein:`);
    
    if (userInput !== confirmText) {
      setMessage({ type: 'error', text: 'Löschung abgebrochen - Eingabe stimmt nicht überein' });
      return;
    }

    try {
      await adminAPI.deleteUser(user.id);
      setMessage({ type: 'success', text: 'Benutzer erfolgreich gelöscht' });
      loadUsers();
      onStatsUpdate();
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Fehler beim Löschen des Benutzers' 
      });
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleUserUpdated = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    loadUsers();
    onStatsUpdate();
    setMessage({ type: 'success', text: 'Benutzer erfolgreich aktualisiert' });
  };

  const handleSort = (key: keyof User) => {
    let direction: 'asc' | 'desc' = 'asc';
    
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
  };

  const getSortedUsers = () => {
    if (!sortConfig.key) return users;

    const sortedUsers = [...users].sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // String comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Number comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }

      // Boolean comparison
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortConfig.direction === 'asc'
          ? (aValue === bValue ? 0 : aValue ? -1 : 1)
          : (aValue === bValue ? 0 : aValue ? 1 : -1);
      }

      return 0;
    });

    return sortedUsers;
  };

  const getSortIcon = (columnKey: keyof User) => {
    if (sortConfig.key !== columnKey) {
      return <span className="sort-icon">⇅</span>;
    }
    return sortConfig.direction === 'asc' 
      ? <span className="sort-icon active">↑</span>
      : <span className="sort-icon active">↓</span>;
  };

  const roles = ['ADMIN', 'MANAGER', 'ACCOUNTING', 'EMPLOYEE'];

  const sortedUsers = getSortedUsers();

  return (
    <div className="user-management">
      <h2><Users /> Benutzerverwaltung</h2>

      {/* Filter Section - NEW DESIGN */}
      <div className="filter-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>Suche:</label>
            <input
              type="text"
              placeholder="Name, E-Mail oder Rolle suchen..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <label>Status:</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="filter-select"
            >
              <option value="">Alle anzeigen</option>
              <option value="active">Nur Aktive</option>
              <option value="inactive">Nur Inaktive</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Rolle:</label>
            <select
              value={filters.role}
              onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
              className="filter-select"
            >
              <option value="">Alle Rollen</option>
              {roles.map(role => (
                <option key={role} value={role}>{getRoleDisplayName(role)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-actions">
          <button 
            className="btn secondary"
            onClick={() => {
              setFilters({ search: '', role: '', status: '' });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <Repeat />
            Filter zurücksetzen
          </button>
          <span className="filter-count">
            {users.length} Benutzer
          </span>
        </div>
      </div>

      {loading ? (
        <div className="loading">Lade Benutzer...</div>
      ) : (
        <>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('firstName')} className="sortable">
                    Name {getSortIcon('firstName')}
                  </th>
                  <th onClick={() => handleSort('email')} className="sortable">
                    E-Mail {getSortIcon('email')}
                  </th>
                  <th onClick={() => handleSort('role')} className="sortable">
                    Rolle {getSortIcon('role')}
                  </th>
                  <th onClick={() => handleSort('isActive')} className="sortable">
                    Status {getSortIcon('isActive')}
                  </th>
                  <th onClick={() => handleSort('twoFactorEnabled')} className="sortable">
                    2FA {getSortIcon('twoFactorEnabled')}
                  </th>
                  {/* <th onClick={() => handleSort('vacationDays')} className="sortable">
                    Urlaubstage {getSortIcon('vacationDays')}
                  </th> */}
                  {/* <th onClick={() => handleSort('weeklyHours')} className="sortable">
                    Wochenstunden {getSortIcon('weeklyHours')}
                  </th> */}
                  {/* <th>Arbeitstage</th> */}
                  <th onClick={() => handleSort('startDate')} className="sortable">
                    Startdatum {getSortIcon('startDate')}
                  </th>
                  {/* <th>NFC ID</th> */}
                  <th onClick={() => handleSort('webTimeTrackingEnabled')} className="sortable">
                    Website-Zeiterfassung {getSortIcon('webTimeTrackingEnabled')}
                  </th>
                  <th onClick={() => handleSort('lastLogin')} className="sortable">
                    Letzter Login {getSortIcon('lastLogin')}
                  </th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map(user => (
                  <tr 
                    key={user.id} 
                    className={`${!user.isActive ? 'inactive-user' : ''} clickable-row`}
                    onClick={() => handleEditUser(user)}
                    title="Klicken zum Bearbeiten"
                  >
                    <td>
                      <div className="user-name">
                        <small>ID: {user.id}</small>
                        <strong> {user.firstName} {user.lastName}</strong>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge ${user.role.toLowerCase()}`}>
                        {getRoleDisplayName(user.role)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td>
                      <span className={`twofa-badge ${user.twoFactorEnabled ? 'enabled' : 'disabled'}`}>
                        {user.twoFactorEnabled ? <Lock /> : <Unlock />}
                      </span>
                    </td>
                    {/* <td>{user.vacationDays} Tage</td> */}
                    {/* <td>{user.weeklyHours}h</td> */}
                    {/* <td>
                      {user.workDays ? user.workDays.split(',').map(day => {
                        const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
                        return dayNames[parseInt(day) - 1];
                      }).filter(Boolean).join(', ') : 'Nicht konfiguriert'}
                    </td> */}
                    <td>
                      <div className="date-info">
                        <span>
                          {user.startDate && !isNaN(new Date(user.startDate).getTime())
                            ? new Date(user.startDate).toLocaleDateString('de-DE')
                            : 'Nicht gesetzt'
                          }
                        </span>
                      </div>
                    </td>
                    {/* <td>
                      <div className="nfc-info">
                        {user.nfcId ? (
                          <span className="nfc-id-set">
                            📱 {user.nfcId}
                          </span>
                        ) : (
                          <span className="nfc-id-empty">
                            🚫 Nicht gesetzt
                          </span>
                        )}
                      </div>
                    </td> */}
                    <td>
                      <div className="web-tracking-info">
                        {user.webTimeTrackingEnabled ? (
                          <span className="web-tracking-enabled">
                            <Laptop /> Erlaubt
                          </span>
                        ) : (
                          <span className="web-tracking-disabled">
                            <SmartphoneNfc /> Nur NFC
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {user.lastLogin 
                        ? new Date(user.lastLogin).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'Nie'
                      }
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="user-actions">
                        <button
                          className="btn small primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditUser(user);
                          }}
                          title="Bearbeiten"
                        >
                          <Edit />
                        </button>
                        
                        <button
                          className={`btn small ${user.isActive ? 'warning' : 'success'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStatus(user);
                          }}
                          title={user.isActive ? 'Deaktivieren' : 'Aktivieren'}
                        >
                          {user.isActive ? <ShieldMinus /> : <ShieldCheck />}
                        </button>

                        {user.twoFactorEnabled && (
                          <button
                            className="btn small warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReset2FA(user);
                            }}
                            title="2FA zurücksetzen"
                          >
                            <Repeat />
                          </button>
                        )}

                        {user.id !== currentUser?.id && (
                          <button
                            className={`btn small ${user._count.workLogs > 0 || user._count.timeOffs > 0 ? 'warning' : 'danger'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteUser(user);
                            }}
                            title={
                              user._count.workLogs > 0 || user._count.timeOffs > 0
                                ? ` WARNUNG: User hat ${user._count.workLogs} Arbeitszeiten und ${user._count.timeOffs} Abwesenheiten`
                                : 'Löschen'
                            }
                          >
                            {user._count.workLogs > 0 || user._count.timeOffs > 0 ? <><OctagonAlert /><Trash /></> : <Trash2 />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn secondary"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                ← Vorherige
              </button>
              
              <span className="pagination-info">
                Seite {pagination.page} von {pagination.totalPages} 
                ({pagination.total} Benutzer insgesamt)
              </span>
              
              <button
                className="btn secondary"
                disabled={pagination.page === pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Nächste →
              </button>
            </div>
          )}
        </>
      )}

      {showEditModal && selectedUser && (
        <UserEditModal
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onUserUpdated={handleUserUpdated}
        />
      )}
    </div>
  );
};